import { Flex } from '@chakra-ui/react';
import { ColDef, GridOptions } from 'ag-grid-community';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import { IMenuItemExportProps } from 'components/buttons/exportButtons/ExportCSVMenuButton';
import { AgGridTable } from 'components/dataTables/agGridTable/AgGridTable';
import EditOpportunityListModal from 'components/dataTables/agGridTable/PlanningTable/components/EditOpportunityListModal';
import {
	IButtonsDefault,
	ICellEdit,
	ICellsEdit,
	IHeader,
} from 'components/dataTables/agGridTable/types/agGrid.type';
import {
	IFilterBody,
	ModulesType,
} from 'components/filter/models/TableFilterModels';
import { Loading360 } from 'components/loading/Loading360';
import NullableTag from 'components/nullableTag/NullableTag';
import { ICycle } from 'models/cycle/cycle.model';
import { HIERARCHY_CONFIGS } from 'pages/admin/demand/planning/components/tabs/planningByLevel/variables/PlanningByLevelMappings';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryTypeModule } from 'services/queryClient/wrapperHooks/genericTables/useGetCheckTableDataWithFilters';
import createMonthListFromCycle from 'utils/functions/createMonthListFromCycle';

import { useGetStatus } from '../hooks/useGetDataGridTableWithFilters';
import TagTypeException from './components/TagTypeException';
import { useChangePageWithConfimation } from './hooks/useChangePageWithConfimation';
import { useExitComponent } from './hooks/useExitComponentWithConfirmation';
import { useUpdatePlanningTable } from './hooks/useUpdatePlanningTable';
import { IBodyPlan, IPlan } from './types/planningTable.type';
import { planningTableMessages } from './variables/planningTableMessages';

const generateColumnsPlan = (
	cycle: ICycle,
	includeOpportunity: boolean,
	nonexistentHierarchyCombinations: boolean
) => {
	const nullableTagRenderer = (params: any) => {
		return (
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					height: '100%',
				}}
			>
				<NullableTag value={params.value} />
			</div>
		);
	};

	const cols: IHeader<IPlan>[] = HIERARCHY_CONFIGS.map(hierarchy => {
		return {
			field: hierarchy.nameColumn + '_name',
			headerName: hierarchy.label,
			sortable: true,
			editable: false,
			type: 'string',
			cellRenderer: nullableTagRenderer,
			enableRowGroup: true,
			enableValue: true,
		};
	});
	if (includeOpportunity) {
		cols.push({
			field: 'exception',
			headerName: 'Exceção',
			sortable: !nonexistentHierarchyCombinations,
			editable: false,
			type: 'string',
			cellRenderer: ({ data }: CustomCellRendererProps<IPlan>) => {
				if (data?.opportunity_id) {
					return (
						<Flex
							alignItems={'center'}
							height={'100%'}
							justifyContent={'center'}
						>
							<TagTypeException tag={'opportunity_exception'} />
						</Flex>
					);
				}
				return <></>;
			},
		});
	}
	const monthsCols = createMonthListFromCycle<IHeader<IPlan>>(
		cycle,
		month => {
			const headerName =
				month.nameAbbreviatedWithYear.charAt(0).toUpperCase() +
				month.nameAbbreviatedWithYear.slice(1);
			return {
				field: 'month_' + month.month,
				headerName,
				sortable: !nonexistentHierarchyCombinations,
				editable: true,
				type: 'number',
				enableValue: true,
				aggFunc: 'sum',
			};
		}
	);
	cols.push(...monthsCols);

	cols.push({
		field: 'total',
		headerName: 'Total',
		sortable: !nonexistentHierarchyCombinations,
		type: 'total',
		editable: false,
		enableValue: true,
		aggFunc: 'sum',
	});

	return cols as any;
};
const getIdCellEdit = (
	{ commercial_hierarchy_id, customer_id, product_id }: IPlan,
	field: string
) =>
	[
		'commercial_hierarchy_id',
		commercial_hierarchy_id,
		'customer_id',
		customer_id,
		'product_id',
		product_id,
		field,
	].join('-');

interface IPlanningTableProps {
	cycle: ICycle;
	module: ModulesType & QueryTypeModule;
	companyAreaId?: number;
	canEdit: boolean;
	renderButtons?: (buttons: IButtonsDefault) => React.ReactNode;
	includeOpportunity?: boolean;
	nameExport: string;
}
export const PlanningTable = ({
	cycle,
	module,
	companyAreaId,
	canEdit,
	renderButtons,
	includeOpportunity = false,
	nameExport,
}: IPlanningTableProps) => {
	const gridRef = useRef<AgGridReact<any>>(null);

	const [opportunity, setOpportunity] = useState<{
		insert: IBodyPlan[];
		opportunityIds: number[];
	}>();

	const [cellEdit, setCellEdit] = useState<ICellsEdit<IPlan>>({});
	const { setIsEditing } = useExitComponent();
	useEffect(() => {
		setIsEditing(Object.keys(cellEdit).length > 0);
	}, [cellEdit, setIsEditing]);
	const convertToBodyPlan = (
		cells: Record<string, ICellEdit<IPlan>>
	): {
		insert: IBodyPlan[];
		opportunityIds: number[];
	} => {
		const opportunityIds: number[] = [];
		const insert: IBodyPlan[] = [];
		for (const key in cells) {
			const cell = cells[key];
			const monthIndex = +cell.colName.replace('month_', '') - 1;
			const year = cycle.ciclo <= monthIndex ? cycle.ano : cycle.ano + 1;

			const monthYear = new Date(year, monthIndex, 1);
			const bodyPlan: IBodyPlan = {
				id: cell.data.id,
				quantity: cell.value,
				month_year: monthYear.toISOString().split('T')[0],
				commercial_hierarchy_id: cell.data.commercial_hierarchy_id,
				customer_id: cell.data.customer_id,
				product_id: cell.data.product_id,
				is_exception: cell.data.is_exception,
				opportunity_id: cell.data.opportunity_id,
			};
			if (cell.data.opportunity_id) {
				opportunityIds.push(cell.data.opportunity_id);
			}
			insert.push(bodyPlan);
		}
		return {
			insert,
			opportunityIds,
		};
	};

	const { mutate } = useUpdatePlanningTable(module, () => {
		setOpportunity(undefined);
		setCellEdit({});
	});
	const save = (insert: IBodyPlan[]) => {
		mutate({
			data: insert,
			cycle_id: cycle.id,
			...(companyAreaId ? { company_area_id: companyAreaId } : {}),
		});
	};
	const submit = (cells: Record<string, ICellEdit<IPlan>>) => {
		const { insert, opportunityIds } = convertToBodyPlan(cells);
		if (opportunityIds.length) {
			setOpportunity({
				insert,
				opportunityIds,
			});
		} else {
			save(insert);
		}
	};
	const [
		nonexistentHierarchyCombinations,
		setNonexistentHierarchyCombinations,
	] = useState(false);
	const headers = useMemo(() => {
		return generateColumnsPlan(
			cycle,
			includeOpportunity,
			nonexistentHierarchyCombinations
		);
	}, [cycle, includeOpportunity, nonexistentHierarchyCombinations]);

	const filtersMap = useMemo(() => {
		const filtersMap = headers.map((column): IFilterBody => {
			return {
				column_name: column.field,
				column_type: column.type === 'string' ? 'string' : 'quantity',
				dictionary_column_name: column.headerName,
				value_type: column.type === 'string' ? 'string' : 'number',
				transform: false,
			};
		});
		if (nonexistentHierarchyCombinations) {
			return filtersMap.filter(filter => filter.value_type !== 'number');
		}
		return filtersMap;
	}, [headers, nonexistentHierarchyCombinations]);
	const disabledEditCell = (colDef: ColDef, data: IPlan): boolean => {
		if (data?.product_id === undefined) {
			return false;
		}
		const isOpportunity = data?.opportunity_id;
		if (isOpportunity) {
			const month = data?.opportunity_month;
			const nameColumn = 'month_' + month;
			return colDef.field === nameColumn;
		}
		return true;
	};

	const [key, setKey] = useState(0);
	const [shouldResetKey, setShouldResetKey] = useState(false);

	useEffect(() => {
		if (shouldResetKey && Object.keys(cellEdit).length === 0) {
			setKey(prev => prev + 1);
			setShouldResetKey(false);
		}
	}, [cellEdit, shouldResetKey]);
	const onCancel = () => {
		setCellEdit({});
		setKey(prev => prev + 1);
		setShouldResetKey(true);
	};
	const { modal: modalExitPage } = useChangePageWithConfimation();

	const getNameExport = useCallback(
		(part: boolean = false) => {
			return `${nameExport}_${+cycle.ciclo}_${cycle.ano}${
				part ? '_part' : ''
			}`;
		},
		[nameExport, cycle.ciclo, cycle.ano]
	);

	const menuList = useMemo((): IMenuItemExportProps<{
		cycle_id: number;
		company_area_id?: number;
	}>[] => {
		const nameExportAndCycle = getNameExport();
		return [
			{
				label: 'Exportar plano completo (todos os registros) para CSV',
				module: module as any,
				filters: {
					cycle: cycle.id.toString(),
					year: cycle.ano.toString(),
				},
				nameExport: nameExportAndCycle,
				params: {
					cycle_id: cycle.id,
					...(companyAreaId
						? { company_area_id: companyAreaId }
						: {}),
				},
				exportAll: true,
			},
		];
	}, [companyAreaId, cycle.ano, cycle.ciclo, cycle.id, module, nameExport]);

	const gridOptions = useMemo((): GridOptions => {
		return {
			groupTotalRow: 'top',
			groupDisplayType: 'multipleColumns',
			groupDefaultExpanded: 0,
			suppressAggFuncInHeader: true,
			sideBar: {
				toolPanels: [
					{
						id: 'columns',
						labelDefault: 'Colunas',
						labelKey: 'columns',
						iconKey: 'columns',
						toolPanel: 'agColumnsToolPanel',
						suppressMenuHide: true,
						toolPanelParams: {
							suppressPivotMode: true,
							suppressRowGroups: false,
							suppressValues: false,
							suppressRowGroupPanel: false,
							suppressPivot: true,
						},
					},
					{
						id: 'rowGroups',
						labelDefault: 'Agrupamento',
						labelKey: 'rowGroups',
						iconKey: 'group',
						toolPanel: 'agRowGroupPanel',
						suppressMenuHide: true,
						toolPanelParams: {
							suppressValues: false,
							suppressPivots: true,
							suppressPivotMode: true,
						},
					},
				],
				position: 'right',
				suppressMenuHide: true,
			} as any,
		};
	}, []);
	const { data: status, isLoading: isLoadingStatus } = useGetStatus(module, {
		cycle_id: cycle.id,
		company_area_id: companyAreaId,
	});
	useEffect(() => {
		if (status === 'saved_zeroed') {
			setNonexistentHierarchyCombinations(true);
		} else if (status !== undefined) {
			setNonexistentHierarchyCombinations(false);
		}
	}, [status, setNonexistentHierarchyCombinations]);
	return !isLoadingStatus ? (
		<Flex key={key}>
			<AgGridTable<
				IPlan,
				IBodyPlan,
				{ cycle_id: number; company_area_id?: number }
			>
				key={
					cycle.id +
					module +
					(nonexistentHierarchyCombinations ? '1' : '0')
				}
				gridOptions={gridOptions}
				noDataMessage={{
					title: planningTableMessages.MSG_NO_DATA_TITLE,
					description: planningTableMessages.MSG_NO_DATA_DESCRIPTION,
				}}
				onCancel={onCancel}
				canEdit={canEdit}
				gridRef={gridRef}
				disabledEditCell={disabledEditCell}
				module={module}
				editable={true}
				getIdCellEdit={getIdCellEdit}
				otherParameters={{
					cycle_id: cycle.id,
					company_area_id: companyAreaId,
				}}
				headers={headers}
				isSubmit={false}
				mutate={submit}
				filtersMap={filtersMap}
				nonexistentHierarchyCombinations={
					nonexistentHierarchyCombinations
				}
				setNonexistentHierarchyCombinations={
					setNonexistentHierarchyCombinations
				}
				getNameExport={() => getNameExport(true)}
				model={
					opportunity && (
						<EditOpportunityListModal
							opportunityIds={opportunity?.opportunityIds}
							cleanOpportunity={() => {
								setOpportunity(undefined);
							}}
							save={() => save(opportunity.insert)}
						/>
					)
				}
				cellEdit={cellEdit}
				setCellEdit={setCellEdit}
				keyDefinedRow={[
					'commercial_hierarchy_id',
					'customer_id',
					'product_id',
				]}
				convertToBody={convertToBodyPlan}
				renderButtons={renderButtons}
				menuList={menuList}
				status={status}
			/>
			{modalExitPage}
		</Flex>
	) : (
		<Flex>
			<Loading360
				size={48}
				variant='gradient'
				table={true}
				message={true}
			/>
		</Flex>
	);
};
